import type * as ngrxStore from '@ngrx/store'

import { Action } from '../actions'
import { Feature } from '../feature'

/**
 * Applies all of our non-standard NgRx reducers
 */
export const META_REDUCER: ngrxStore.MetaReducer =
  reducer => (state, action) => {
    // Applies the base reducer from NgRx (this is the combination of
    // all reducers registered with NgRx)
    const reducedState: GlobalState = {
      // NOTE: in some cases, the reducer function seems to delete all
      // dynamically added states, so we need to add them back here
      ...state,
      ...reducer(state, action),
    }

    if (Action.isAction(action) && !action.getFeature().lifecycle.isDestroyed) {
      // If this action is one of our actions and its feature has not been
      // destroyed, we need to apply its reducer
      _writeFeatureStateToGlobalState(
        reducedState,
        action.featureId,
        action.reducer(
          // NOTE: it is possible the state for this feature doesn't exist yet, in
          // which case we need to use the initial state
          reducedState[action.featureId] ?? action.getFeature().initialState,
          reducedState,
        ),
      )

      // And we need to apply any extra reducers that apply to this action
      for (const { feature, reducer: extraReducer } of Feature.extraReducers) {
        if (extraReducer.on.some(filter => filter.check(action))) {
          // And if there are any extra reducers that apply, we need to apply
          // those as well
          _writeFeatureStateToGlobalState(
            reducedState,
            feature.id,
            extraReducer.reduce(
              // NOTE: it is possible the state for this feature doesn't exist yet, in
              // which case we need to use the initial state
              reducedState[feature.id] ?? feature.initialState,
              action,
              reducedState,
            ),
          )
        }
      }
    }
    console.debug('REDUCER', action.type, action, reducedState) // TODO: remove

    // Finally, we return the reduced state with all of its
    // modifications complete
    return reducedState
  }

function _writeFeatureStateToGlobalState(
  globalState: GlobalState,
  featureId: string,
  featureState: unknown,
): void {
  if (featureState === undefined) {
    // If the feature state is `undefined`, that means we should delete the
    // feature state altogether
    delete globalState[featureId]
  } else {
    // Otherwise, we write the feature state to the global state
    globalState[featureId] = featureState
  }
}

type GlobalState = Partial<Record<string, unknown>>
